import type { KeyValueStore } from './key-value-store';

/**
 * Parent-initiated delete, as **physical erasure** (BL-003, ruled by D-106 PS6-3).
 *
 * A grown-up asking for their family's data to be deleted is not asking for it to be forgotten by
 * this session. Logical clearing — an empty list written over the top, a flag that says "deleted" —
 * leaves the child's name, their progress and their evidence rows physically on the disk under the
 * same keys, recoverable by anything that reads storage directly. So this uses the same primitive
 * the decontamination pass uses, with the same requirement: `requireRemoval`, which is not satisfied
 * by a tombstone overwrite (D-032(3)).
 *
 * And it **reads back**. An erasure that reports success because a `removeItem` did not throw is a
 * claim about an API call; what a parent is owed is a claim about the disk. Every key is asked for
 * again afterwards, through `readPhysical` — which goes to the backend handle rather than to this
 * session's overlay — and anything still present is reported as still present. The operation is
 * allowed to say it did not finish; it is not allowed to say it finished when it did not.
 *
 * **Both stores.** LocalStorage is where everything lives today, and IndexedDB is one line of code
 * away from being where a cache lands. Deleting one and not the other would make "delete my data"
 * true of the place we happen to remember.
 */

export interface FamilyErasureResult {
  /** Keys that were found and are now physically gone, confirmed by reading them back. */
  readonly erased: readonly string[];
  /** Keys that survived the attempt. Empty is the only good answer, and it is checked, not assumed. */
  readonly remaining: readonly string[];
  /** Databases deleted, confirmed by asking for the list again. */
  readonly databasesDeleted: readonly string[];
  /**
   * Databases still there afterwards (P6R1-5).
   *
   * Reported separately rather than folded into a count, because a screen that only knows how many
   * *keys* survived will happily say "0 records are still stored" about a sitting whose database is
   * demonstrably still on the disk.
   */
  readonly remainingDatabases: readonly string[];
  /**
   * Whether this delete was able to **look** (P6R1-4, P6R1-5).
   *
   * Distinct from `complete`, and the distinction is the whole finding: "nothing is left" and "I
   * could not tell what is left" are different answers, and only one of them may be reported to a
   * parent as success. It is false when there is no physical store to inspect — a browser that
   * blocks storage throws on the *property*, so the store is built with no handle and
   * `physicalKeys()` truthfully answers "the store I was given is empty" about a store that does
   * not exist — and false when the database inventory is unavailable or refused.
   *
   * `hasPhysicalStore` is the right question here precisely because it cannot be wrong in the
   * unsafe direction (S1-5): it asks whether there is anywhere for bytes to *be*, which no
   * session-local read failure can change.
   */
  readonly checked: boolean;
  /**
   * Whether the family's data is *known* to be gone.
   *
   * False when anything survived, when an erasure is still pending, when the namespace walk could
   * not finish, and whenever `checked` is false. "Probably deleted" is not an answer to give a
   * parent (the `NamespaceEnumeration.complete` fail-closed rule, S1-4).
   */
  readonly complete: boolean;
}

/** The IndexedDB surface this needs, named so a test can present one without a browser. */
export interface DatabaseFactory {
  databases?: () => Promise<{ name?: string }[]>;
  deleteDatabase: (name: string) => { onsuccess: unknown; onerror: unknown };
}

/**
 * What databases exist, and **whether that could be established** (P6R1-5).
 *
 * `indexedDB.databases()` is not universally implemented and can reject. Round 1 turned both into
 * an empty list, which is a claim the browser never made: "there are no databases" and "this
 * browser will not tell me" are the same value and opposite facts. They are separate answers now,
 * and the unavailable one is treated fail-closed everywhere it is read.
 */
interface DatabaseInventory {
  readonly names: readonly string[];
  readonly complete: boolean;
}

async function listDatabases(factory: DatabaseFactory | undefined): Promise<DatabaseInventory> {
  // No factory at all is not a failure: this build supports being handed one, and a caller with no
  // IndexedDB in the environment has nothing to inventory. A factory that *has* no `databases()`
  // is the unknown case — there is a store, and it will not say what is in it.
  if (factory === undefined) return { names: [], complete: true };
  if (factory.databases === undefined) return { names: [], complete: false };
  try {
    return {
      names: (await factory.databases())
        .map((entry) => entry.name ?? '')
        .filter((name) => name !== ''),
      complete: true,
    };
  } catch {
    // A browser that will not list its databases has not told us there are none.
    return { names: [], complete: false };
  }
}

async function deleteDatabase(factory: DatabaseFactory, name: string): Promise<void> {
  await new Promise<void>((resolve) => {
    try {
      const request = factory.deleteDatabase(name) as {
        onsuccess: (() => void) | null;
        onerror: (() => void) | null;
        onblocked?: (() => void) | null;
      };
      request.onsuccess = () => {
        resolve();
      };
      request.onerror = () => {
        resolve();
      };
      if ('onblocked' in request) {
        request.onblocked = () => {
          resolve();
        };
      }
    } catch {
      resolve();
    }
  });
}

/**
 * Erases everything this family has stored, and then checks.
 *
 * Runs inside `withDeferredCallbacks` so no `onWarning` handler can interleave with the sequence —
 * the same protection the decontamination pass takes, and for the same reason (R5-1).
 */
export async function eraseFamilyData(
  store: KeyValueStore,
  factory?: DatabaseFactory,
): Promise<FamilyErasureResult> {
  const enumeration = store.withDeferredCallbacks(() => {
    const found = store.physicalKeys();
    for (const key of found.keys) store.erase(key, { requireRemoval: true });
    return found;
  });

  const before = await listDatabases(factory);
  if (factory !== undefined) {
    for (const name of before.names) await deleteDatabase(factory, name);
  }
  const afterDatabases = await listDatabases(factory);

  /**
   * The read-back, taken **after every piece of erase work, not part way through it** (P6R15-2).
   *
   * It used to be recorded the moment LocalStorage was clear, while the database work was still to
   * come — and `complete` was then computed from that older look. So anything written during the
   * remaining work was invisible to the certification, and a parent could be told "deleted, and
   * checked" about a world that had changed since it was checked.
   *
   * The answer has to describe the world as this operation leaves it, which means asking last.
   * Deliberately a *fresh* enumeration as well as a per-key read: a key this build has no name for
   * is exactly the kind that must not survive a parent's delete.
   */
  const remaining = new Set(
    enumeration.keys.filter((key) => store.readPhysical(key).status !== 'absent'),
  );
  const after = store.physicalKeys();
  for (const key of after.keys) remaining.add(key);
  for (const key of store.pendingErasures()) remaining.add(key);
  const survivors = new Set(afterDatabases.names);
  const databasesDeleted = before.names.filter((name) => !survivors.has(name));

  /**
   * Could this delete look at all? (P6R1-4, P6R1-5, completed by D-109(4).)
   *
   * Every source of "I do not know" in one place, and every one of them fails closed.
   *
   * Round 1 had a `store.hasPhysicalStore` conjunct here as well, because `physicalKeys()` then
   * answered "empty, and I looked" about a store it was never given. That defect is fixed at its
   * own layer now — a walk that never started reports `complete: false` — so the conjunct is
   * **deleted** rather than left as a second copy of one rule. One owner: the enumeration says
   * whether it could see, and this says what that means for a parent.
   */
  const checked =
    enumeration.complete && after.complete && before.complete && afterDatabases.complete;

  return {
    erased: enumeration.keys.filter((key) => !remaining.has(key)),
    remaining: [...remaining],
    databasesDeleted,
    remainingDatabases: [...survivors],
    checked,
    complete: checked && remaining.size === 0 && survivors.size === 0,
  };
}
